import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";

export interface DryRunRequest {
  name?: string;
  funnelId?: number | string;
  subdomain?: string;
}

export interface DryRunResponse {
  ok: boolean;
  issues: string[];
  funnelIdAvailable: boolean;
  suggestedFunnelId: number;
  subdomainAvailable: boolean;
  suggestedSubdomain: string;
}

export type ProjectWorkspace = "agentur" | "marketingabteilung";

export interface CreateProjectInput {
  name: string;
  funnelId?: number;
  subdomain?: string;
  allowedDomains?: string[];
  /** Optionale DHC-Kundenwahl (dh_oneclick kunden.id) — leer = kein fester Kunde. */
  kundeRecId?: string | null;
  workspace?: ProjectWorkspace;
  externalId?: string | null;
}

/**
 * Ob die DHC-Kunde-Verknüpfung für diesen Workspace angeboten wird — für
 * marketingabteilung.ai-Projekte hat dh_portal keine passende Rolle (TASK-142).
 */
export function offersKundeLink(workspace: ProjectWorkspace): boolean {
  return workspace !== "marketingabteilung";
}

/**
 * Ob die Herkunfts-Hinweise (Projekt-Anlage über marketingabteilung.ai,
 * Endpunkt-Aktivierung „vom Kunden"/„vom Team") angezeigt werden — nur
 * marketingabteilung.ai-Projekte haben eine Partner-Herkunft (TASK-147).
 */
export function showsPartnerOrigin(workspace: ProjectWorkspace): boolean {
  return workspace === "marketingabteilung";
}

export interface CreatedProject {
  id: number;
  name: string;
  funnelId: number;
  funnelMark: string;
  subdomain: string;
  bqDataset: string;
  apiSecret: string;
  trackerUrl: string;
  scriptTag: string;
  createdAt: string;
  kundeRecId?: string | null;
  workspace: ProjectWorkspace;
  externalId: string | null;
}

/** Ergebnis der Rückschreibung in dh_portal.PortalConfig. */
export interface KundeLinkResult {
  ok: boolean;
  mode: "updated" | "created" | "skipped";
  error?: string;
}

export interface DhCustomer {
  kundeRecId: string;
  company: string | null;
}

/** DHC-Kundenliste für die Auswahl im Anlageflow (leer, wenn nicht konfiguriert). */
export function useDhCustomers() {
  return useQuery<{ configured: boolean; customers: DhCustomer[] }>({
    queryKey: ["admin", "dh-customers"],
    queryFn: () =>
      api<{ configured: boolean; customers: DhCustomer[] }>("/admin/dh-customers"),
    staleTime: 5 * 60_000,
  });
}

export function useDryRun(input: DryRunRequest, enabled: boolean) {
  return useQuery<DryRunResponse>({
    queryKey: ["admin", "projects", "dry-run", input],
    queryFn: () =>
      api<DryRunResponse>("/admin/projects/dry-run", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    enabled,
    staleTime: 5_000,
  });
}

export function useCreateProject() {
  return useMutation({
    mutationFn: (input: CreateProjectInput) =>
      api<{ ok: true; project: CreatedProject; kundeLink: KundeLinkResult | null }>(
        "/admin/projects",
        {
          method: "POST",
          body: JSON.stringify(input),
        },
      ),
  });
}

export interface CustomHostnameStatus {
  registered: boolean;
  hostname: string | null;
  cfStatus: string | null;
  sslStatus: string | null;
  ownershipVerification: {
    type: string;
    name: string;
    value: string;
  } | null;
  validationRecords: Array<{
    txt_name?: string;
    txt_value?: string;
    http_url?: string;
    http_body?: string;
  }>;
  /** Cloudflare's own reason for a non-verified hostname, verbatim. */
  verificationErrors?: string[];
  workerRouteActive: boolean;
}

export function useCustomHostnameStatus(
  projectId: number | null,
  refetchMs: number | false = false,
) {
  return useQuery<CustomHostnameStatus>({
    queryKey: ["admin", "projects", projectId, "custom-hostname"],
    queryFn: () =>
      api<CustomHostnameStatus>(`/admin/projects/${projectId}/custom-hostname`),
    enabled: projectId !== null,
    refetchInterval: refetchMs,
    refetchIntervalInBackground: true,
  });
}

function invalidateProject(qc: ReturnType<typeof useQueryClient>, projectId: number) {
  qc.invalidateQueries({
    queryKey: ["admin", "projects", projectId, "custom-hostname"],
  });
  qc.invalidateQueries({ queryKey: ["admin", "projects", "detail", projectId] });
  qc.invalidateQueries({ queryKey: ["admin", "projects", "list"] });
}

export function useRegisterCustomHostname(projectId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (hostname: string) =>
      api<{ ok: true; status: CustomHostnameStatus }>(
        `/admin/projects/${projectId}/custom-hostname`,
        { method: "POST", body: JSON.stringify({ hostname }) },
      ),
    onSuccess: () => {
      if (projectId !== null) invalidateProject(qc, projectId);
    },
  });
}

/**
 * Verify button: make Cloudflare re-check the hostname (TASK-40).
 *
 * Cloudflare keeps its verdict from registration time, so a customer who sets
 * the DNS records afterwards leaves the hostname pending forever. Merely asking
 * for the status — what this button used to do — returns that frozen answer
 * every time. Only a write unsticks it.
 */
export function useRevalidateCustomHostname(projectId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{
        ok: boolean;
        revalidateError: string | null;
        status: CustomHostnameStatus;
      }>(`/admin/projects/${projectId}/custom-hostname/revalidate`, {
        method: "POST",
      }),
    onSuccess: () => {
      if (projectId !== null) invalidateProject(qc, projectId);
    },
  });
}

export function useDeleteCustomHostname(projectId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ ok: true; removed: boolean }>(
        `/admin/projects/${projectId}/custom-hostname`,
        { method: "DELETE" },
      ),
    onSuccess: () => {
      if (projectId !== null) invalidateProject(qc, projectId);
    },
  });
}
